import React, { useState, useEffect } from 'react';
import {
  Settings, Mail, Save, Loader2, CheckCircle2,
  AlertCircle, RefreshCw, Eye, EyeOff, Send, Zap
} from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

const CAMPOS_SMTP = [
  { chave: 'smtp_host',   label: 'Servidor SMTP',     placeholder: 'smtp.gmail.com',          tipo: 'text' },
  { chave: 'smtp_port',   label: 'Porta',              placeholder: '587',                      tipo: 'number' },
  { chave: 'smtp_user',   label: 'Usuário / E-mail',  placeholder: 'seuemail@gmail.com',       tipo: 'text' },
  { chave: 'smtp_pass',   label: 'Senha / App Password', placeholder: '••••••••••••',          tipo: 'password' },
  { chave: 'smtp_from',   label: 'Remetente',          placeholder: 'Kairós <nome@dominio.com>', tipo: 'text' },
  { chave: 'portal_url',  label: 'URL do Portal',      placeholder: 'https://storage.kairossistemas.app.br', tipo: 'text' },
];

export default function ConfiguracoesPage() {
  const [valores,           setValores]           = useState({});
  const [loading,           setLoading]           = useState(true);
  const [salvando,          setSalvando]          = useState(false);
  const [testando,          setTestando]          = useState(false);
  const [senhaVis,          setSenhaVis]          = useState(false);
  const [smtpSecure,        setSmtpSecure]        = useState(false);
  const [nfedistAmb,        setNfedistAmb]        = useState('1');
  const [emailAtivo,        setEmailAtivo]        = useState(false);
  const [syncCrmAtivo,      setSyncCrmAtivo]      = useState(true);
  const [nfedistAtivo,      setNfedistAtivo]      = useState(true);

  useEffect(() => { carregar(); }, []);

  async function carregar() {
    try {
      const { data } = await api.get('/configuracoes');
      const mapa = Object.fromEntries(data.map(r => [r.chave, r.valor || '']));
      setValores(mapa);
      setSmtpSecure(mapa.smtp_secure === 'true');
      setNfedistAmb(mapa.nfedist_amb || '1');
      setEmailAtivo(mapa.email_boas_vindas_ativo === 'true');
      setSyncCrmAtivo(mapa.sync_crm_ativo !== 'false');
      setNfedistAtivo(mapa.nfedist_ativo !== 'false');
    } catch {
      toast.error('Erro ao carregar configurações.');
    } finally {
      setLoading(false);
    }
  }

  async function salvar() {
    setSalvando(true);
    try {
      const payload = {
        ...valores,
        smtp_secure:            smtpSecure   ? 'true' : 'false',
        nfedist_amb:            nfedistAmb,
        email_boas_vindas_ativo: emailAtivo   ? 'true' : 'false',
        sync_crm_ativo:         syncCrmAtivo  ? 'true' : 'false',
        nfedist_ativo:          nfedistAtivo  ? 'true' : 'false',
      };
      // Não enviar senha mascarada
      if (payload.smtp_pass && payload.smtp_pass.includes('•')) {
        delete payload.smtp_pass;
      }
      await api.put('/configuracoes', payload);
      toast.success('Configurações salvas!');
      await carregar(); // recarrega para atualizar máscaras
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao salvar.');
    } finally {
      setSalvando(false);
    }
  }

  async function testarEmail() {
    setTestando(true);
    const t = toast.loading('Enviando e-mail de teste...');
    try {
      const { data } = await api.post('/configuracoes/testar-email');
      toast.success(data.mensagem, { id: t });
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Falha no envio.', { id: t });
    } finally {
      setTestando(false);
    }
  }

  if (loading) return (
    <div className="flex justify-center py-20">
      <Loader2 size={28} className="animate-spin text-primary-500" />
    </div>
  );

  const smtpConfigured = valores.smtp_user && !valores.smtp_pass?.includes('•')
    ? true
    : valores.smtp_user && valores.smtp_pass?.includes('•');  // senha mascarada = já estava salva

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Cabeçalho */}
      <div className="flex items-center gap-2">
        <Settings size={18} className="text-primary-400" />
        <h1 className="text-base font-semibold text-slate-200">Configurações</h1>
      </div>

      {/* ── SMTP ───────────────────────────────────────────── */}
      <div className="card space-y-4">
        <div className="flex items-center gap-2 mb-1">
          <Mail size={16} className="text-sky-400" />
          <h2 className="text-sm font-semibold text-slate-300">E-mail / SMTP</h2>
          {smtpConfigured
            ? <span className="ml-auto flex items-center gap-1 text-xs text-emerald-400">
                <CheckCircle2 size={13} /> Configurado
              </span>
            : <span className="ml-auto flex items-center gap-1 text-xs text-amber-400">
                <AlertCircle size={13} /> Não configurado
              </span>
          }
        </div>

        <p className="text-xs text-slate-500">
          Usado para enviar o e-mail de boas-vindas quando um novo cliente é criado pelo sync do CRM.
          Para Gmail, crie um <strong className="text-slate-400">App Password</strong> nas configurações de segurança da conta.
        </p>

        <div className="grid grid-cols-1 gap-3">
          {CAMPOS_SMTP.map(({ chave, label, placeholder, tipo }) => (
            <div key={chave} className="grid grid-cols-3 items-center gap-3">
              <label className="text-xs text-slate-400 text-right">{label}</label>
              <div className="col-span-2 relative">
                <input
                  className="input text-sm w-full"
                  type={tipo === 'password' && !senhaVis ? 'password' : 'text'}
                  placeholder={placeholder}
                  value={valores[chave] || ''}
                  onChange={e => setValores(v => ({ ...v, [chave]: e.target.value }))}
                />
                {tipo === 'password' && (
                  <button
                    type="button"
                    onClick={() => setSenhaVis(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                    {senhaVis ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                )}
              </div>
            </div>
          ))}

          {/* TLS Direto */}
          <div className="grid grid-cols-3 items-center gap-3">
            <label className="text-xs text-slate-400 text-right">TLS Direto (porta 465)</label>
            <div className="col-span-2 flex items-center gap-2">
              <button
                type="button"
                onClick={() => setSmtpSecure(v => !v)}
                className={`w-10 h-5 rounded-full transition-colors flex items-center ${smtpSecure ? 'bg-primary-600' : 'bg-slate-700'}`}>
                <span className={`w-4 h-4 rounded-full bg-white shadow transition-transform mx-0.5 ${smtpSecure ? 'translate-x-5' : 'translate-x-0'}`} />
              </button>
              <span className="text-xs text-slate-500">{smtpSecure ? 'Ativado (SSL porta 465)' : 'Desativado (STARTTLS porta 587)'}</span>
            </div>
          </div>
        </div>

        {/* Ações SMTP */}
        <div className="flex items-center gap-2 pt-2 border-t border-surface-border">
          <button
            onClick={testarEmail}
            disabled={testando || !smtpConfigured}
            className="btn-secondary text-xs py-1.5 px-3 disabled:opacity-50">
            {testando ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
            Testar Envio
          </button>
          <span className="text-xs text-slate-600">Envia um e-mail de teste para o seu endereço.</span>
        </div>
      </div>

      {/* ── NFeDistDFe Ambiente ───────────────────────────── */}
      <div className="card space-y-3">
        <div className="flex items-center gap-2 mb-1">
          <RefreshCw size={16} className="text-violet-400" />
          <h2 className="text-sm font-semibold text-slate-300">NFeDistDFe / Manifestação</h2>
        </div>
        <div className="grid grid-cols-3 items-center gap-3">
          <label className="text-xs text-slate-400 text-right">Ambiente SEFAZ</label>
          <div className="col-span-2">
            <select
              className="input text-sm"
              value={nfedistAmb}
              onChange={e => setNfedistAmb(e.target.value)}>
              <option value="1">Produção</option>
              <option value="2">Homologação (testes)</option>
            </select>
          </div>
        </div>
        <p className="text-xs text-slate-600">
          Afeta NFeDistDFe (download de XMLs) e Manifestação eletrônica. Use Homologação apenas para testes.
        </p>
      </div>

      {/* ── Automações ────────────────────────────────────── */}
      <div className="card space-y-4">
        <div className="flex items-center gap-2 mb-1">
          <Zap size={16} className="text-amber-400" />
          <h2 className="text-sm font-semibold text-slate-300">Automações</h2>
        </div>
        <p className="text-xs text-slate-500">
          Controle quais processos automáticos estão ativos. Qualquer alteração entra em vigor no próximo disparo do cron — sem precisar reiniciar o servidor.
        </p>

        {[
          {
            label:     'Sync CRM → Storage (cron 03h)',
            sublabel:  'Cria empresas e usuários automaticamente todo dia às 3h.',
            valor:     syncCrmAtivo,
            onChange:  setSyncCrmAtivo,
          },
          {
            label:     'Download NFeDistDFe (cron 06h e 18h)',
            sublabel:  'Consulta SEFAZ e baixa XMLs de compras para cada empresa com certificado.',
            valor:     nfedistAtivo,
            onChange:  setNfedistAtivo,
          },
          {
            label:     'E-mail de boas-vindas ao criar usuário',
            sublabel:  'Envia e-mail com login e senha quando um novo cliente/contador é criado pelo sync.',
            valor:     emailAtivo,
            onChange:  setEmailAtivo,
            warning:   !emailAtivo ? 'Desativado — nenhum e-mail será enviado ao criar usuários.' : null,
          },
        ].map(({ label, sublabel, valor, onChange, warning }) => (
          <div key={label} className="flex items-start justify-between gap-4 py-3 border-t border-surface-border first:border-0 first:pt-0">
            <div className="flex-1">
              <p className="text-sm text-slate-300">{label}</p>
              <p className="text-xs text-slate-500 mt-0.5">{sublabel}</p>
              {warning && (
                <p className="text-xs text-amber-400 mt-1 flex items-center gap-1">
                  <AlertCircle size={11} /> {warning}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => onChange(v => !v)}
              className={`w-11 h-6 rounded-full transition-colors flex items-center flex-shrink-0 mt-0.5 ${valor ? 'bg-primary-600' : 'bg-slate-700'}`}>
              <span className={`w-5 h-5 rounded-full bg-white shadow transition-transform mx-0.5 ${valor ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>
        ))}
      </div>

      {/* Botão salvar global */}
      <div className="flex justify-end">
        <button onClick={salvar} disabled={salvando} className="btn-primary py-2 px-6">
          {salvando
            ? <><Loader2 size={15} className="animate-spin" /> Salvando...</>
            : <><Save size={15} /> Salvar Configurações</>
          }
        </button>
      </div>
    </div>
  );
}
